<div class="container-fluid complyon-page doa-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell doa-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title doa-title mb-2 text-l">Delegation of authority</h1>
      <p class="complyon-subtitle doa-subtitle mb-0 text-s">
        The authoritative rulebook for approval thresholds and decision rights. OnboardOS executes these rules at intake; Complyon owns the rules.
      </p>
    </section>

    <section class="d-flex flex-column flex-xl-row align-items-start justify-content-between gap-3 mb-3 doa-toolbar">
      <div class="d-flex flex-wrap align-items-center gap-2 doa-tab-row" role="tablist" aria-label="Delegation of authority tabs">
        <button
          v-for="tab in doa_tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn d-inline-flex align-items-center gap-2"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn btn-link text-decoration-none doa-reset-btn d-inline-flex align-items-center gap-2">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'overview'">
      <section class="row g-3 mb-3">
        <div
          v-for="card in summary_cards"
          :key="card.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article
            class="complyon-card doa-card rounded-4 p-3 h-100"
            :class="card.tab_id ? 'cursor-pointer' : ''"
            :role="card.tab_id ? 'button' : null"
            :tabindex="card.tab_id ? 0 : null"
            @click="open_summary_card(card)"
            @keydown.enter="open_summary_card(card)"
            @keydown.space.prevent="open_summary_card(card)"
          >
            <div class="complyon-stat-label doa-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value doa-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : 'text-warning-emphasis'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note doa-stat-note text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel doa-resolver-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start gap-3 px-3 px-md-4 pt-3 pt-md-4">
          <span class="doa-resolver-icon d-inline-flex align-items-center justify-content-center">
            <i class="bi bi-diagram-3"></i>
          </span>
          <div class="min-w-0">
            <div class="doa-resolver-title">Authority resolver</div>
            <div class="doa-resolver-subtitle">Enter a transaction — Complyon computes who must approve, applying thresholds, risk routing, delegations and segregation of duties.</div>
          </div>
        </div>

        <div class="px-3 px-md-4 pt-3">
          <div class="row g-3">
            <div
              v-for="field in authority_fields"
              :key="field.label"
              :class="field.label === 'Requested by (optional)' ? 'col-12 col-xl-3' : field.label === 'Amount (R)' ? 'col-12 col-md-6 col-xl-2' : field.label === 'Scenario' ? 'col-12 col-md-6 col-xl-3' : field.label === 'Risk level' ? 'col-12 col-md-6 col-xl-2' : 'col-12 col-md-6 col-xl-3'"
            >
              <label class="doa-field-label text-xs">{{ field.label }}</label>
              <div v-if="field.type === 'select'">
                <select class="form-select doa-form-control" :aria-label="field.label" v-model="field.value">
                  <option
                    v-for="option in field.options"
                    :key="`${field.label}-${option}`"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </div>
              <div v-else>
                <input
                  class="form-control doa-form-control"
                  type="text"
                  :placeholder="field.placeholder"
                  v-model="field.value"
                />
              </div>
            </div>
          </div>

          <div class="doa-divider my-3"></div>

          <div class="d-flex flex-column flex-xl-row align-items-start justify-content-between gap-3">
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span class="badge rounded-pill doa-threshold-pill">R 500 000 - R 5 000 000</span>
                <span class="badge rounded-pill doa-status-pill is-green">Authorised by: CFO + Compliance + Internal Audit</span>
              </div>

              <div class="d-flex flex-wrap align-items-center gap-2 mb-3 doa-role-route-row">
                <span
                  v-for="role in route_roles"
                  :key="role.label"
                  class="badge rounded-pill doa-route-pill"
                  :class="`is-${role.tone}`"
                >
                  <span>{{ role.label }}</span>
                  <small v-if="role.suffix" class="ms-1">{{ role.suffix }}</small>
                </span>
              </div>

              <div class="doa-note-row d-flex align-items-start gap-2 mb-2">
                <i class="bi bi-exclamation-triangle"></i>
                <div>
                  <strong>Risk routing applied:</strong> Any high-risk transaction — added Compliance, Internal Audit.
                </div>
              </div>

              <div class="doa-note-row d-flex align-items-start gap-2">
                <i class="bi bi-check2-square"></i>
                <div>
                  <strong>Segregation of duties:</strong> Requester is separate from all approvers.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="doa-resolver-footer px-3 px-md-4 py-3 mt-3 border-top d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <i class="bi bi-diagram-3 doa-footer-icon"></i>
            <span>This same engine is exposed as <code class="doa-inline-code">ComplyonAuthority.resolve()</code> — every module routes approvals through it, none decide on their own.</span>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2">
            <i class="bi bi-send"></i>
            <span>Route & log this decision</span>
          </button>
        </div>
      </section>

      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Approval control plane</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">Every module below routes its approvals through this engine instead of deciding on its own. That single source of truth is what makes each decision defensible in an audit.</div>
          </div>
        </div>

        <div class="px-3 px-md-4 pb-4 pt-3">
          <div class="d-flex flex-wrap align-items-center gap-2">
            <span
              v-for="app in control_plane_apps"
              :key="app.label"
              class="badge rounded-pill doa-app-pill d-inline-flex align-items-center gap-2"
              :class="app.route_path ? 'cursor-pointer' : ''"
              :role="app.route_path ? 'button' : null"
              :tabindex="app.route_path ? 0 : null"
              @click="open_app_route(app.route_path)"
              @keydown.enter="open_app_route(app.route_path)"
              @keydown.space.prevent="open_app_route(app.route_path)"
            >
              <i class="bi" :class="app.icon"></i>
              <span>{{ app.label }}</span>
            </span>
          </div>
        </div>
      </section>

      <section class="doa-banner rounded-4 px-3 px-md-4 py-3">
        <i class="bi bi-shield-check"></i>
        <p class="mb-0">
          Complyon owns the rules; OnboardOS executes them. These thresholds and routing rules are enforced at intake by OnboardOS and on every approval across Complyon — proving each decision was made by an authorised person.
        </p>
      </section>
    </div>

    <div v-else-if="active_tab === 'approval_matrix'">
      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Approval matrix</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">Who can approve what - by amount band, department, category and risk level.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('approval_rule')">
            <i class="bi bi-plus-lg"></i>
            <span>Add rule</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Transaction</th>
                <th class="text-xs">Category</th>
                <th class="text-xs">Dept</th>
                <th class="text-xs">Band / condition</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Required approval</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in approval_rows"
                :key="row.transaction"
                class="table-row"
                role="button"
                tabindex="0"
                @click="open_approval_matrix_edit(row)"
                @keydown.enter="open_approval_matrix_edit(row)"
                @keydown.space.prevent="open_approval_matrix_edit(row)"
              >
                <td class="ps-3">
                  <div class="doa-name">{{ row.transaction }}</div>
                </td>
                <td>{{ row.category }}</td>
                <td>{{ row.dept }}</td>
                <td>{{ row.band }}</td>
                <td>
                  <span class="badge complyon-badge doa-badge text-xs" :class="row.risk_class">{{ row.risk }}</span>
                </td>
                <td>{{ row.approval }}</td>
                <td class="pe-3 workflows-arrow text-end">
                  <button type="button" class="btn btn-link text-decoration-none p-0 text-secondary" aria-label="Edit approval rule" @click.stop="open_approval_matrix_edit(row)">
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'financial_thresholds'">
      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Financial thresholds</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">The amount bands and the authority each requires. These drive the resolver and OnboardOS intake gates.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('financial_threshold')">
            <i class="bi bi-plus-lg"></i>
            <span>Add band</span>
          </button>
        </div>

        <div class="px-3 px-md-4 pb-4 pt-3">
          <div v-for="group in financial_threshold_groups" :key="group.label" class="mb-4">
            <div class="fw-semibold mb-2">{{ group.label }}</div>
            <div class="row g-2">
              <div v-for="card in group.cards" :key="`${group.label}-${card.band}`" class="col-12 col-md-6 col-xl-3">
                <article
                  class="doa-threshold-card rounded-3 p-3 h-100 cursor-pointer"
                  role="button"
                  tabindex="0"
                  @click="open_financial_threshold_edit(group, card)"
                  @keydown.enter="open_financial_threshold_edit(group, card)"
                  @keydown.space.prevent="open_financial_threshold_edit(group, card)"
                >
                  <div class="doa-threshold-band">{{ card.band }}</div>
                  <div class="doa-threshold-approver">{{ card.approver }}</div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'procurement_contracts'">
      <section v-for="panel in procurement_authority_groups" :key="panel.title" class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">{{ panel.title }}</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">{{ panel.description }}</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal(panel.modal_key)">
            <i class="bi bi-plus-lg"></i>
            <span>Add authority</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Action</th>
                <th class="text-xs">Condition</th>
                <th class="text-xs">Approver</th>
                <th class="text-xs">Evidence</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in panel.rows"
                :key="`${panel.title}-${row.action}`"
                class="table-row"
                role="button"
                tabindex="0"
                @click="open_procurement_authority_edit(panel, row)"
                @keydown.enter="open_procurement_authority_edit(panel, row)"
                @keydown.space.prevent="open_procurement_authority_edit(panel, row)"
              >
                <td class="ps-3"><div class="doa-name">{{ row.action }}</div></td>
                <td>{{ row.condition }}</td>
                <td>{{ row.approver }}</td>
                <td>{{ row.evidence }}</td>
                <td class="pe-3 workflows-arrow text-end"><button type="button" class="btn btn-link text-decoration-none p-0 text-secondary" aria-label="Edit authority" @click.stop="open_procurement_authority_edit(panel, row)"><i class="bi bi-chevron-right"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'risk_routing'">
      <section class="doa-banner rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-diagram-3"></i>
        <p class="mb-0">
          <strong>Risk-based routing.</strong> High-risk suppliers, high-value contracts and personal-data sharing automatically pull Compliance, Legal and Internal Audit into the approval chain - no manual routing.
        </p>
      </section>

      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Risk-routing rules</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">When a trigger fires, these approvers are automatically added to the required chain.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('risk_routing')">
            <i class="bi bi-plus-lg"></i>
            <span>Add rule</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Trigger</th>
                <th class="text-xs">Risk</th>
                <th class="text-xs">Auto-adds</th>
                <th class="text-xs">Rationale</th>
                <th class="text-xs">On</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in risk_routing_rows"
                :key="row.trigger"
                class="table-row"
                role="button"
                tabindex="0"
                @click="open_risk_routing_edit(row)"
                @keydown.enter="open_risk_routing_edit(row)"
                @keydown.space.prevent="open_risk_routing_edit(row)"
              >
                <td class="ps-3"><div class="doa-name">{{ row.trigger }}</div></td>
                <td><span class="badge complyon-badge doa-badge text-xs" :class="row.risk === 'High' ? 'bg-danger-subtle text-danger-emphasis' : 'bg-warning-subtle text-warning-emphasis'">{{ row.risk }}</span></td>
                <td>
                  <div class="d-flex flex-wrap gap-1">
                    <span v-for="item in row.adds" :key="`${row.trigger}-${item}`" class="badge rounded-pill bg-light text-secondary border">{{ item }}</span>
                  </div>
                </td>
                <td>{{ row.rationale }}</td>
                <td><span class="badge rounded-pill bg-success-subtle text-success-emphasis">{{ row.status }}</span></td>
                <td class="pe-3 workflows-arrow text-end"><button type="button" class="btn btn-link text-decoration-none p-0 text-secondary" aria-label="Edit routing rule" @click.stop="open_risk_routing_edit(row)"><i class="bi bi-chevron-right"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'delegations'">
      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Temporary delegations</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">When someone is on leave, their authority is delegated with start and end dates. Active delegations re-point the resolver automatically.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('delegation')">
            <i class="bi bi-plus-lg"></i>
            <span>Delegate authority</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Delegator</th>
                <th class="text-xs">Authority</th>
                <th class="text-xs">Delegate</th>
                <th class="text-xs">Scope</th>
                <th class="text-xs">Period</th>
                <th class="text-xs">Status</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in delegations_table_rows"
                :key="row.delegator"
                class="table-row"
                role="button"
                tabindex="0"
                @click="open_delegation_edit(row)"
                @keydown.enter="open_delegation_edit(row)"
                @keydown.space.prevent="open_delegation_edit(row)"
              >
                <td class="ps-3"><div class="doa-name">{{ row.delegator }}</div></td>
                <td>{{ row.authority }}</td>
                <td>{{ row.delegate }}</td>
                <td>{{ row.scope }}</td>
                <td>{{ row.period }}</td>
                <td><span class="badge complyon-badge doa-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 workflows-arrow text-end"><button type="button" class="btn btn-link text-decoration-none p-0 text-secondary" aria-label="Edit delegation" @click.stop="open_delegation_edit(row)"><i class="bi bi-chevron-right"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'sod'">
      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Segregation of duties - rules</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">The same person must not request, approve and pay. Each process splits duties across different people.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('sod_rule')">
            <i class="bi bi-plus-lg"></i>
            <span>Add rule</span>
          </button>
        </div>

        <div class="px-3 px-md-4 pb-4 pt-3">
          <div class="row g-3">
            <div v-for="card in sod_rule_cards" :key="card.title" class="col-12 col-xl-4">
              <article
                class="complyon-card doa-card rounded-4 p-3 h-100 cursor-pointer"
                role="button"
                tabindex="0"
                @click="open_sod_rule_edit(card)"
                @keydown.enter="open_sod_rule_edit(card)"
                @keydown.space.prevent="open_sod_rule_edit(card)"
              >
                <div class="d-flex align-items-start justify-content-between gap-2 mb-2">
                  <div class="fw-semibold">{{ card.title }}</div>
                  <span class="badge rounded-pill bg-success-subtle text-success-emphasis">{{ card.status }}</span>
                </div>
                <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                  <template v-for="(chip, index) in card.chips" :key="`${card.title}-${chip}`">
                    <span class="badge rounded-pill doa-route-pill is-teal"><span>{{ chip }}</span></span>
                    <span v-if="index < card.chips.length - 1" class="text-secondary fw-semibold">x</span>
                  </template>
                </div>
                <div class="small text-secondary">{{ card.note }}</div>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Detected conflicts</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">Live segregation-of-duties breaches where one person held conflicting duties.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('sod_conflict')">
            <i class="bi bi-plus-lg"></i>
            <span>Log conflict</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Person</th>
                <th class="text-xs">Process</th>
                <th class="text-xs">Conflict</th>
                <th class="text-xs">Severity</th>
                <th class="text-xs">Status</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in sod_conflict_rows"
                :key="row.person"
                class="table-row"
                role="button"
                tabindex="0"
                @click="open_sod_conflict_edit(row)"
                @keydown.enter="open_sod_conflict_edit(row)"
                @keydown.space.prevent="open_sod_conflict_edit(row)"
              >
                <td class="ps-3"><div class="doa-name">{{ row.person }}</div></td>
                <td>{{ row.process }}</td>
                <td>{{ row.conflict }}</td>
                <td><span class="badge complyon-badge doa-badge text-xs" :class="row.severity_class">{{ row.severity }}</span></td>
                <td><span class="badge complyon-badge doa-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 workflows-arrow text-end"><button type="button" class="btn btn-link text-decoration-none p-0 text-secondary" aria-label="Edit conflict" @click.stop="open_sod_conflict_edit(row)"><i class="bi bi-chevron-right"></i></button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'live_decisions'">
      <section class="doa-banner rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-diagram-3"></i>
        <p class="mb-0">
          <strong>Every approval is routed here, not decided in the module.</strong>
          The authority engine computes the required chain; the verdict below compares who actually approved against that chain. This is the audit-defensible record that no decision was made outside delegated authority.
        </p>
      </section>

      <section class="row g-3 mb-3">
        <div
          v-for="card in live_decision_stats"
          :key="card.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <article
            class="complyon-card doa-card rounded-4 p-3 h-100"
            :class="card.tab_id ? 'cursor-pointer' : ''"
            :role="card.tab_id ? 'button' : null"
            :tabindex="card.tab_id ? 0 : null"
            @click="open_summary_card(card)"
            @keydown.enter="open_summary_card(card)"
            @keydown.space.prevent="open_summary_card(card)"
          >
            <div class="complyon-stat-label doa-stat-label text-xs">{{ card.label }}</div>
            <div
              class="complyon-stat-value doa-stat-value text-l"
              :class="card.accent === 'red' ? 'text-danger' : card.accent === 'green' ? 'text-success' : card.accent === 'amber' ? 'text-warning-emphasis' : 'text-dark'"
            >
              {{ card.value }}
            </div>
            <div class="complyon-stat-note doa-stat-note text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="px-3 px-md-4 pt-3 pt-md-4">
          <div class="complyon-section-title doa-section-title text-s">Live decisions</div>
          <div class="complyon-section-subtitle doa-section-subtitle text-xs">
            Real transactions routed through Delegation of Authority. Open one for the computed chain and the end-to-end trace across modules.
          </div>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs doa-table-col-ref">Ref</th>
                <th class="text-xs">Transaction</th>
                <th class="text-xs doa-table-col-amount">Amount</th>
                <th class="text-xs doa-table-col-risk">Risk</th>
                <th class="text-xs">Required chain (engine)</th>
                <th class="text-xs doa-table-col-verdict">Verdict</th>
                <th class="pe-3 text-xs doa-table-col-actions"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in live_decision_rows"
                :key="row.ref"
                :class="{ 'table-row': row.drawer_key }"
                :role="row.drawer_key ? 'button' : null"
                :tabindex="row.drawer_key ? 0 : null"
                @click="row.drawer_key ? open_drawer(row.drawer_key) : null"
                @keydown.enter="row.drawer_key ? open_drawer(row.drawer_key) : null"
                @keydown.space.prevent="row.drawer_key ? open_drawer(row.drawer_key) : null"
              >
                <td class="ps-3">
                  <div class="doa-name text-nowrap">{{ row.ref }}</div>
                </td>
                <td>
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <div class="doa-name">{{ row.transaction }}</div>
                    <button
                      v-if="row.id_chip"
                      type="button"
                      class="btn btn-link text-decoration-none p-0"
                      :class="row.id_chip.route_path ? 'cursor-pointer' : ''"
                      @click.stop="open_app_route(row.id_chip.route_path)"
                      :aria-label="`Open ${row.id_chip.label}`"
                    >
                      <span class="badge rounded-pill doa-id-chip">{{ row.id_chip.label }}</span>
                    </button>
                  </div>
                </td>
                <td>
                  <div class="doa-name text-nowrap">{{ row.amount }}</div>
                </td>
                <td>
                  <span class="badge complyon-badge doa-badge text-xs" :class="row.risk_class">{{ row.risk }}</span>
                </td>
                <td>
                  <div class="doa-name-subtitle">{{ row.required_chain }}</div>
                </td>
                <td>
                  <span class="badge complyon-badge doa-badge text-xs" :class="row.verdict_class">{{ row.verdict }}</span>
                </td>
                <td class="pe-3 workflows-arrow text-end">
                  <button
                    type="button"
                    class="btn btn-link text-decoration-none p-0 text-secondary"
                    :aria-label="`Open ${row.ref}`"
                    @click.stop="row.drawer_key ? open_drawer(row.drawer_key) : null"
                  >
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'exception_log'">
      <section class="doa-banner rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-flag"></i>
        <p class="mb-0">
          <strong>Exception log.</strong> Every approval made outside the authority matrix is recorded here, with the proper authority it bypassed, the reason, and the escalation - the audit trail that proves deviations were caught.
        </p>
      </section>

      <section class="complyon-panel doa-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="complyon-section-title doa-section-title text-s">Authority exceptions</div>
            <div class="complyon-section-subtitle doa-section-subtitle text-xs">Approvals outside the matrix - recorded and escalated.</div>
          </div>
          <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="open_modal('authority_exception')">
            <i class="bi bi-plus-lg"></i>
            <span>Log exception</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table complyon-table doa-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Transaction</th>
                <th class="text-xs">Value</th>
                <th class="text-xs">Approved by</th>
                <th class="text-xs">Should have been</th>
                <th class="text-xs">Escalated to</th>
                <th class="text-xs">Status</th>
                <th class="pe-3 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in exception_requests"
                :key="item.transaction"
                class="table-row"
                :role="'button'"
                tabindex="0"
                @click="item.drawer_key ? open_drawer(item.drawer_key) : null"
                @keydown.enter="item.drawer_key ? open_drawer(item.drawer_key) : null"
                @keydown.space.prevent="item.drawer_key ? open_drawer(item.drawer_key) : null"
              >
                <td class="ps-3">
                  <div class="doa-name">{{ item.transaction }}</div>
                </td>
                <td>{{ item.value }}</td>
                <td>{{ item.approved_by }}</td>
                <td>{{ item.should_have_been }}</td>
                <td>{{ item.escalated_to }}</td>
                <td>
                  <span class="badge complyon-badge doa-badge text-xs" :class="item.status.class">{{ item.status.label }}</span>
                </td>
                <td class="pe-3 workflows-arrow">
                  <button type="button" class="btn btn-link text-decoration-none p-0 text-secondary" aria-label="Open exception drawer" @click.stop="item.drawer_key ? open_drawer(item.drawer_key) : null">
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else>
      <section class="doa-empty-state rounded-4">
        <div class="text-secondary">This tab is wired and ready, but the screenshot-equivalent content is kept on Overview.</div>
      </section>
    </div>
  </div>

  <div
    v-if="active_modal"
    class="doa-modal-backdrop"
    @click="close_modal"
  ></div>

  <section
    v-if="active_modal"
    class="doa-modal-shell"
    role="dialog"
    aria-modal="true"
    :aria-label="active_modal.title"
    @click.stop
  >
    <div class="doa-modal-header border-bottom">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <h2 class="doa-modal-title mb-1">{{ active_modal.title }}</h2>
          <div class="doa-modal-subtitle">{{ active_modal.subtitle }}</div>
        </div>
        <button
          type="button"
          class="btn btn-link text-decoration-none p-0 doa-modal-close"
          aria-label="Close modal"
          @click="close_modal"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <div class="doa-modal-body">
      <div class="row g-3">
        <div
          v-for="field in active_modal.fields"
          :key="field.label"
          :class="field.class || 'col-12'"
        >
          <label class="doa-field-label text-xs">{{ field.label }}</label>

          <select
            v-if="field.type === 'select'"
            class="form-select doa-form-control"
            :aria-label="field.label"
            v-model="field.value"
          >
            <option
              v-for="option in field.options"
              :key="`${active_modal.title}-${field.label}-${option}`"
              :value="option"
            >
              {{ option }}
            </option>
          </select>

          <textarea
            v-else-if="field.type === 'textarea'"
            class="form-control doa-form-control"
            :placeholder="field.placeholder"
            rows="3"
            v-model="field.value"
          ></textarea>

          <input
            v-else
            class="form-control doa-form-control"
            type="text"
            :placeholder="field.placeholder"
            v-model="field.value"
          />
        </div>
      </div>
    </div>

    <div class="doa-modal-footer border-top d-flex align-items-center justify-content-end gap-2">
      <button v-if="active_modal.secondary_label" type="button" class="btn btn-link text-decoration-none me-auto text-danger">
        <i class="bi bi-trash3 me-1"></i>
        <span>{{ active_modal.secondary_label }}</span>
      </button>
      <button type="button" class="btn btn-link text-decoration-none" @click="close_modal">Cancel</button>
      <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2">
        <i class="bi" :class="active_modal.secondary_label ? 'bi-check2-square' : 'bi-plus-lg'"></i>
        <span>{{ active_modal.primary_label || 'Create' }}</span>
      </button>
    </div>
  </section>

  <div
    v-if="active_drawer"
    class="command-centre-drawer-backdrop"
    @click="close_drawer"
  ></div>

  <aside
    v-if="active_drawer"
    class="command-centre-drawer"
    aria-label="Delegation of authority drawer"
  >
    <div class="command-centre-drawer-header border-bottom">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="command-centre-drawer-kicker text-uppercase">{{ active_drawer.section }}</div>
          <h2 class="command-centre-drawer-title mb-1 text-truncate">{{ active_drawer.title }}</h2>
          <div class="command-centre-drawer-subtitle text-truncate">{{ active_drawer.subtitle }}</div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="badge rounded-pill" :class="active_drawer.status_class">{{ active_drawer.status }}</span>
          <button
            type="button"
            class="btn btn-link text-decoration-none p-0 command-centre-drawer-close"
            @click="close_drawer"
            aria-label="Close drawer"
          >
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </div>

    <div ref="drawerBody" class="command-centre-drawer-body">
      <div v-if="active_drawer.chain">
        <div class="command-centre-drawer-meta mb-4">
          <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div class="command-centre-drawer-section-title">{{ active_drawer.chain_label }}</div>
            <div class="command-centre-drawer-kicker text-uppercase text-success">{{ active_drawer.chain_note }}</div>
          </div>
          <div class="d-flex flex-wrap align-items-center gap-2">
            <template v-for="item in active_drawer.chain" :key="item.label">
              <span class="badge rounded-pill doa-route-pill" :class="`is-${item.tone}`">
                <span>{{ item.label }}</span>
                <small v-if="item.suffix" class="ms-1">{{ item.suffix }}</small>
              </span>
              <span v-if="item !== active_drawer.chain[active_drawer.chain.length - 1]" class="text-secondary fw-semibold">+</span>
            </template>
          </div>
        </div>

        <div class="command-centre-drawer-meta mb-4">
          <div class="command-centre-drawer-section-title mb-2">{{ active_drawer.verdict_title }}</div>
          <div class="p-3 rounded-3 border" :class="active_drawer.verdict_tone">
            <div class="fw-semibold mb-1">{{ active_drawer.status }}</div>
            <div class="small">{{ active_drawer.verdict_body }}</div>
          </div>
        </div>

        <section class="mb-4">
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div class="command-centre-drawer-section-title">{{ active_drawer.trace_title }}</div>
          </div>
          <div class="d-flex flex-column gap-2">
            <article
              v-for="item in active_drawer.trace"
              :key="item.title"
              class="command-centre-drawer-item d-flex align-items-start justify-content-between gap-3"
            >
              <div class="d-flex align-items-start gap-2 min-w-0">
                <span class="command-centre-drawer-item-icon d-inline-flex align-items-center justify-content-center is-green">
                  <i class="bi bi-check2-circle"></i>
                </span>
                <div class="min-w-0">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div class="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <span class="badge rounded-pill bg-light text-secondary border">{{ item.tag }}</span>
                  </div>
                  <div class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                </div>
              </div>
            </article>
          </div>
        </section>
      </div>

      <div v-else-if="active_drawer.details || active_drawer.sections">
        <div
          v-if="active_drawer.details_layout === 'list'"
          class="command-centre-drawer-details-list mb-4"
        >
          <div
            v-for="row in active_drawer.details"
            :key="row.label"
            class="command-centre-drawer-details-row d-flex align-items-start justify-content-between gap-3"
          >
            <div class="command-centre-drawer-meta-label text-nowrap">{{ row.label }}</div>
            <div class="command-centre-drawer-meta-value text-end" :class="row.value_class || ''">{{ row.value }}</div>
          </div>
        </div>

        <section
          v-for="section in active_drawer.sections"
          :key="section.title"
          class="mb-4"
        >
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div class="command-centre-drawer-section-title">{{ section.title }}</div>
            <span class="badge rounded-pill" :class="section.badge_class">{{ section.badge }}</span>
          </div>

          <div v-if="section.kind === 'items'" class="d-flex flex-column gap-2">
            <article
              v-for="item in section.items"
              :key="item.title"
              class="command-centre-drawer-item d-flex align-items-start justify-content-between gap-3"
            >
              <div class="d-flex align-items-start gap-2 min-w-0">
                <span :class="['command-centre-drawer-item-icon d-inline-flex align-items-center justify-content-center', item.icon_class]">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                  <div class="command-centre-drawer-item-meta">{{ item.meta }}</div>
                </div>
              </div>
              <span
                v-if="item.badge"
                class="badge rounded-pill text-nowrap fw-semibold"
                :class="item.badge_class"
              >
                {{ item.badge }}
              </span>
            </article>
          </div>

          <div v-else-if="section.kind === 'timeline'" class="position-relative ps-1">
            <div class="command-centre-drawer-timeline">
              <article
                v-for="item in section.items"
                :key="item.title"
                :class="[
                  'command-centre-drawer-timeline-item',
                  item.connected ? 'is-connected' : ''
                ]"
              >
                <span :class="['command-centre-drawer-timeline-badge', item.icon_class]">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <span v-if="item.connected" class="command-centre-drawer-timeline-line"></span>
                <div class="command-centre-drawer-timeline-content">
                  <div class="command-centre-drawer-item-title">{{ item.title }}</div>
                  <div v-if="item.subtitle" class="command-centre-drawer-item-subtitle">{{ item.subtitle }}</div>
                  <div v-if="item.meta" class="command-centre-drawer-timeline-meta">
                    <span>{{ item.meta }}</span>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div v-else-if="section.kind === 'progress'" class="d-flex flex-column gap-3">
            <div
              v-for="item in section.items"
              :key="item.label"
              class="command-centre-drawer-progress-row"
            >
              <div class="d-flex align-items-center justify-content-between gap-3 mb-2">
                <div class="d-flex align-items-center gap-2 min-w-0">
                  <span class="command-centre-drawer-dot rounded-circle flex-shrink-0" :class="get_progress_dot_class(item)"></span>
                  <div class="command-centre-drawer-item-title text-truncate">{{ item.label }}</div>
                </div>
                <div class="d-flex align-items-center gap-3 flex-shrink-0">
                  <span class="command-centre-drawer-progress-value">{{ item.percent }}%</span>
                  <span class="command-centre-drawer-progress-count">{{ item.count }}</span>
                </div>
              </div>
              <div class="progress rounded-pill command-centre-drawer-progress">
                <div class="progress-bar bg-success command-centre-drawer-progress-fill" :class="get_progress_width_class(item.percent)"></div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div v-else>
        <div class="h-100 d-flex align-items-center justify-content-center text-center px-3">
          <div>
            <div class="command-centre-drawer-empty-icon d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-secondary mb-3">
              <i class="bi bi-info-circle"></i>
            </div>
            <div class="fw-semibold text-dark mb-1">{{ active_drawer.body_title }}</div>
            <div class="small text-secondary">
              {{ active_drawer.body_copy }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="active_drawer.footer_label || (active_drawer.footer_actions && active_drawer.footer_actions.length)" class="command-centre-drawer-footer border-top">
      <div class="d-flex flex-wrap gap-2 align-items-center">
        <div v-if="active_drawer.footer_label" class="ms-auto d-flex align-items-center">
          <button
            type="button"
            class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2"
          >
            <i class="bi" :class="active_drawer.footer_icon || 'bi-box-arrow-up-right'"></i>
            <span>{{ active_drawer.footer_label }}</span>
          </button>
        </div>

        <div
          v-if="active_drawer_footer_primary_actions.length"
          class="command-centre-drawer-footer-left d-flex align-items-center gap-2"
        >
          <button
            v-for="action in active_drawer_footer_primary_actions"
            :key="action.label"
            type="button"
            class="btn d-inline-flex align-items-center gap-2"
            :class="action.variant === 'primary'
              ? 'config-navbar-primary-action'
              : action.variant === 'secondary'
                ? 'config-navbar-secondary-action'
                : action.variant === 'outline-danger'
                  ? 'btn-outline-danger'
                  : action.variant === 'outline-warning'
                    ? 'btn-outline-warning'
                    : action.variant === 'outline-secondary'
                      ? 'btn-outline-secondary'
                      : 'btn-link text-decoration-none text-secondary px-0'"
          >
            <i class="bi" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </button>
        </div>

        <div class="command-centre-drawer-footer-actions ms-auto d-flex flex-wrap gap-2 align-items-center">
          <button
            v-for="action in active_drawer_footer_actions"
            :key="action.label"
            type="button"
            class="btn d-inline-flex align-items-center gap-2"
            :class="action.variant === 'primary'
              ? 'config-navbar-primary-action'
              : action.variant === 'secondary'
                ? 'config-navbar-secondary-action'
                : action.variant === 'outline-danger'
                  ? 'btn-outline-danger'
                  : action.variant === 'outline-warning'
                    ? 'btn-outline-warning'
                    : action.variant === 'outline-secondary'
                      ? 'btn-outline-secondary'
                      : 'btn-link text-decoration-none text-secondary px-0'"
          >
            <i class="bi" :class="action.icon"></i>
            <span>{{ action.label }}</span>
          </button>
        </div>
      </div>
    </div>
  </aside>
</div>
